import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import DocumentTemplateService from "../../_services/DocumentTemplateService";
import TenantPreferencesService from "../../_services/TenantPreferencesService";
import StarterTemplatePickerModal from "../email-templates/components/StarterTemplatePickerModal";
// Reuse the email builder's card styling for visual parity (premium card grid).
import "../email-templates/email-templates.css";

/**
 * Document Templates — invoice / receipt / SOA templates. Same premium card grid
 * as Email Templates, but these open the shared GrapesJS builder with the
 * Mustache variable palette, and one can be marked the active invoice template.
 */
const DocumentTemplatesPage: React.FC = () => {
  const history = useHistory();
  const [templates, setTemplates] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [showStarter, setShowStarter] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tplRes, cfg] = await Promise.all([
        DocumentTemplateService.list(),
        TenantPreferencesService.getInvoiceConfig().catch(() => ({ activeTemplateId: null })),
      ]);
      setTemplates(tplRes.data?.data || []);
      setActiveId(cfg.activeTemplateId ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateBlank = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await DocumentTemplateService.create({
        Name: "Untitled Invoice Template",
        // Seed a minimal invoice scaffold so the row has a Mustache body and the
        // editor opens with something to build on.
        Mustache:
          `<div style="font-family:Arial,sans-serif;max-width:640px;margin:0 auto;padding:24px">` +
          `<img src="{{logoUrl}}" alt="Logo" style="max-height:56px"/>` +
          `<h2>Invoice {{invoiceNumber}}</h2>` +
          `<p>Billed to: {{invoiceName}}<br/>{{{invoiceAddressDisplay}}}</p>` +
          `<p>Date: {{invoiceDate}} &nbsp; Due: {{invoiceDueDate}}</p>` +
          `<p><strong>Total payable: {{currency}} {{toBePaid}}</strong></p>` +
          `</div>`,
      });
      const newId = res.data?.data?.Id;
      if (newId) history.push(`/document-templates/${newId}/edit`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to create template");
    } finally {
      setBusy(false);
    }
  };

  const handleStarterSelect = async (starter: any) => {
    setBusy(true);
    setError(null);
    try {
      const res = await DocumentTemplateService.create({
        Name: starter.name,
        Mustache: starter.html,
      });
      const newId = res.data?.data?.Id;
      setShowStarter(false);
      if (newId) history.push(`/document-templates/${newId}/edit`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to create from standard");
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (t: any) => {
    try {
      const res = await DocumentTemplateService.duplicate(t.Id);
      const newId = res.data?.data?.Id;
      if (newId) history.push(`/document-templates/${newId}/edit`);
      else load();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to duplicate template");
    }
  };

  const handleSetActive = async (t: any) => {
    setBusy(true);
    setError(null);
    try {
      const res = await TenantPreferencesService.saveInvoiceConfig(t.Id);
      setActiveId(res.activeTemplateId ?? t.Id);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to set active template");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await DocumentTemplateService.remove(deleteConfirm.Id);
      if (activeId === deleteConfirm.Id) {
        await TenantPreferencesService.saveInvoiceConfig(null).catch(() => undefined);
        setActiveId(null);
      }
      load();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to delete template");
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="email-templates-page document-templates-page">
      <div className="et-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => history.push("/tenant-preferences?tab=receipt")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 10px",
              background: "var(--brand-primary-subtle)",
              border: "1px solid var(--brand-primary-subtle)",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              color: "var(--brand-primary-hover)",
              fontWeight: 500,
            }}
            title="Tenant Preferences — Receipt & Invoice"
          >
            ⚙ Receipt &amp; Invoice
          </button>
          <div>
            <h2 style={{ margin: 0 }}>Document Templates</h2>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Invoice, receipt &amp; statement templates. The one marked{" "}
              <strong>Invoice default</strong> is what renders generated invoices.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={handleCreateBlank} disabled={busy}>
            Blank Template
          </Button>
          <Button variant="primary" onClick={() => setShowStarter(true)} disabled={busy}>
            + New from Standard
          </Button>
        </div>
      </div>

      {error && (
        <div
          style={{
            margin: "0 0 16px",
            padding: "10px 14px",
            background: "var(--status-danger-bg)",
            border: "1px solid var(--status-danger-border)",
            color: "var(--status-danger)",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="et-empty">
          <h3>No document templates yet</h3>
          <p>Start from a standard invoice or receipt design, then tweak it in the visual builder.</p>
          <Button variant="primary" onClick={() => setShowStarter(true)} disabled={busy}>
            Browse Standard Templates
          </Button>
        </div>
      ) : (
        <div className="et-grid">
          {templates.map((t: any) => {
            const isActive = activeId === t.Id;
            return (
              <div key={t.Id} className="et-card">
                <div
                  className="et-card-preview"
                  onClick={() => history.push(`/document-templates/${t.Id}/edit`)}
                >
                  {t.MessagePreview ? (
                    <div
                      className="et-preview-content"
                      dangerouslySetInnerHTML={{ __html: t.MessagePreview }}
                    />
                  ) : (
                    <div className="et-preview-empty">No content</div>
                  )}
                  {t.hasDesign ? <span className="et-badge-builder">Builder</span> : null}
                  {isActive && (
                    <span
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 8,
                        fontSize: 10,
                        fontWeight: 700,
                        color: "var(--text-on-brand)",
                        background: "var(--status-success)",
                        padding: "2px 7px",
                        borderRadius: 4,
                        pointerEvents: "none",
                      }}
                    >
                      Invoice default
                    </span>
                  )}
                  <span
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 8,
                      fontSize: 10,
                      color: "var(--text-on-brand)",
                      background: "rgba(0,0,0,0.45)",
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontFamily: "monospace",
                      pointerEvents: "none",
                    }}
                    title="Template ID"
                  >
                    #{t.Id}
                  </span>
                </div>
                <div className="et-card-info">
                  <div className="et-card-name" title={t.Name}>
                    {t.Name}
                  </div>
                </div>
                <div className="et-card-actions">
                  <button
                    className="et-action-btn"
                    onClick={() => history.push(`/document-templates/${t.Id}/edit`)}
                  >
                    Edit
                  </button>
                  <button className="et-action-btn" onClick={() => handleDuplicate(t)}>
                    Duplicate
                  </button>
                  <button
                    className="et-action-btn"
                    onClick={() => handleSetActive(t)}
                    disabled={isActive || busy}
                    title="Use this template for generated invoices"
                  >
                    {isActive ? "Default ✓" : "Set default"}
                  </button>
                  <button
                    className="et-action-btn et-action-delete"
                    onClick={() => setDeleteConfirm(t)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StarterTemplatePickerModal
        show={showStarter}
        onHide={() => setShowStarter(false)}
        onSelect={handleStarterSelect}
        title="Choose a Standard Template"
        loadStarters={async () => {
          const res = await DocumentTemplateService.getStarters();
          return res.data?.data || [];
        }}
      />

      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteConfirm?.Name}</strong>?
          {activeId === deleteConfirm?.Id && (
            <div style={{ marginTop: 8, color: "var(--status-danger)", fontSize: 13 }}>
              This is the active invoice template — deleting it will revert invoices to the
              built-in default layout until you pick another.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentTemplatesPage;
